





















































import { Mixins, Component, Watch, Inject } from "vue-property-decorator";
import {
  ITicketFeedProvider,
  ITicketFeedProviderMethods,
  ITicketProvider
} from "@/models/providers/ticket";
import { TicketStatusCodes, TicketStatusMap } from "@/data/enums/tickets";
import Context from "@/mixins/context";
import LoggedUserMixin from "@/mixins/loggedUserMixin";
import { HookCodes } from "@/data/enums/hooks";

@Component({
  components: {
    "ticket-message-group": () =>
      import("@/components/app/global/tickets/ticketMessageGroup.vue")
  }
})
export default class TicketMessages extends Mixins(Context, LoggedUserMixin) {
  // Inject

  @Inject({ from: "ticketProvider" })
  public readonly $ticketData!: () => ITicketProvider;

  @Inject({ from: "ticketFeedProviderData" })
  public readonly $ticketFeedData!: () => ITicketFeedProvider;

  @Inject({ from: "ticketFeedProviderMethods" })
  public readonly $ticketFeedMethods!: ITicketFeedProviderMethods;

  // Data

  public activeTab = "all";
  public isLoading = false;
  public isLoadingMore = false;

  // Computed

  public get ticketData() {
    return this.$ticketData();
  }

  public get ticketFeedData() {
    return this.$ticketFeedData();
  }

  public get tabs() {
    return this.$_.filter(
      [
        {
          key: "all",
          label: this.$t("_.all") as string,
          if: () => true,
          loadMore: this.$ticketFeedMethods.loadAll,
          include: ["messages", "logs"],
          noResultsMsg: this.$t("_sentence.ticket_no_messages_msg")
        },
        {
          key: "attachments",
          label: this.$t("_.attachments") as string,
          if: () => true,
          loadMore: this.$ticketFeedMethods.loadAttachments,
          include: ["messages"],
          noResultsMsg: this.$t("_sentence.no_attachment_msg")
        },
        {
          key: "notes",
          label: this.$t("_.notes") as string,
          if: () => this.isAdmin,
          loadMore: this.$ticketFeedMethods.loadNotes,
          include: ["messages"],
          noResultsMsg: this.$t("_sentence.no_internal_notes_msg")
        }
      ],
      tab => tab.if()
    );
  }

  public get tab() {
    return this.$_.find(this.tabs, tab => tab.key === this.activeTab) as any;
  }

  public get orderedData() {
    let data;
    data = this.$_.pick(this.ticketFeedData, this.tab?.include);
    data = this.$_.values(data);
    data = this.$_.union(...data);
    data = this.$_.uniqBy(data, "id");

    return this.$_.orderBy(
      data,
      ["created_at", entry => this.$_.has(entry, "hook_id")],
      ["desc", "desc"]
    );
  }

  public mapMessageData(data, participants) {
    const isLog = this.$_.has(data, "hook_id");
    const isClient = !isLog && !!data.client_id;
    const isAuthor = [data.client_id, data.user_id].includes(
      this.loggedUser.id
    );
    const authorKey = isLog
      ? null
      : data.client_id
      ? `c_${data.client_id}`
      : `u_${data.user_id}`;
    participants.push(authorKey);
    participants = this.$_.compact(this.$_.uniq(participants));
    return {
      isLog,
      isMessage: !isLog,
      isInternal: this.$_.get(data, "is_private", false),
      isClient,
      clientId: data?.client_id,
      userId: data?.user_id,
      isAgent: !isClient && !isLog,
      isDeleted: data.is_log,
      isDeleteable: this.isAdmin
        ? this.ticketData.canDeleteTicketMessage
        : isAuthor && !this.$_.without(participants, authorKey).length,
      isEditable: this.isAdmin
        ? this.ticketData.canUpdateTicketMessage
        : isAuthor && !this.$_.without(participants, authorKey).length,
      hasBeenEdited: data.created_at !== data.updated_at,
      dateUpdated: data.updated_at,
      authorKey,
      authorName: data.client_name || data.user_name || null,
      authorImageUrl: data.client_image_url || data.user_image_url || null,
      id: data.id,
      body: isLog
        ? this.$t("_.status_changed_to", {
            status: this.$_.get(
              this.status(data.hook.code),
              "label",
              data.hook.code
            )
          })
        : data.body,
      timestamp: data.created_at,
      files: data.files,
      reason: data.reason,
      actorName: data.actor_name,
      source_type: data.source_type,
      source_ip: data.source_ip,
      email_file: data.email_file
    };
  }

  public get orderedDataMapped() {
    const participants = [];
    return this.$_.map(this.orderedData, data =>
      this.mapMessageData(data, participants)
    );
  }

  public get orderedDataGrouped() {
    return this.orderedDataMapped.reduce((prev: any[], curr) => {
      if (prev.length) {
        const last = prev[prev.length - 1][0];
        if (last.authorKey === curr.authorKey) {
          prev[prev.length - 1].push(curr);
          return prev;
        }
      }
      prev.push([curr]);
      return prev;
    }, []);
  }

  public get statusMap() {
    return TicketStatusMap();
  }

  public switchToAll() {
    this.activeTab = "all";
  }

  public status(hook) {
    switch (hook) {
      case HookCodes.TICKET_CLOSED:
        return this.statusMap[TicketStatusCodes.CLOSED];
      case HookCodes.TICKET_IN_PROGRESS:
        return this.statusMap[TicketStatusCodes.IN_PROGESS];
      case HookCodes.TICKET_CLIENT_REPLIED:
        return this.statusMap[TicketStatusCodes.CLIENT_REPLIED];
      case HookCodes.TICKET_REOPENED:
      case HookCodes.TICKET_OPENED:
        return this.statusMap[TicketStatusCodes.OPEN];
      case HookCodes.TICKET_WAITING_RESPONSE:
        return this.statusMap[TicketStatusCodes.AWAITING_RESPONSE];
      default:
        return null;
    }
  }

  // Watchers

  @Watch("tab", { immediate: true })
  public async onTabChange() {
    this.$ticketFeedMethods.resetFeed();
    this.isLoading = true;
    this.loadMore()?.finally(() => (this.isLoading = false));
  }

  // Methods

  public loadMore() {
    this.isLoadingMore = true;
    return this.tab?.loadMore().finally(() => {
      this.isLoadingMore = false;
    });
  }
}
